/**
 * The HTTP request options object
 */
export interface IHttpRequestOptions {
  /**
   * The request URL
   */
  url: string;

  /**
   * The HTTP headers
   */
  headers?: Record<string, string>;

  /**
   * The request body data
   */
  data?: unknown;

  /**
   * Whether cached responses are allowed
   */
  cache?: boolean;
}

/**
 * The HTTP client contract
 */
export interface IHttpClient {
  /**
   * Gets JSON data from a service
   *
   * @param options - the request options
   * @returns - the response
   */
  getJsonAsync<TResponse>(options: IHttpRequestOptions): Promise<TResponse>;

  /**
   * Posts JSON data to a service and receives a response
   *
   * @param options - the request options
   * @returns - the response
   */
  postJsonAsync<TResponse>(options: IHttpRequestOptions): Promise<TResponse>;
}

/**
 * The HTTP Error from the JSON Service
 */
export class HttpError extends Error {
  /**
   * Initializes a new instance of the HttpError class.
   *
   * @param text - the error text
   * @param statusCode - the HTTP status code
   * @param statusText - the HTTP status text
   */
  public constructor(text: string, public statusCode: number, public statusText: string) {
    super(text);

    // When extending the built-in Error type, you have to fix up the prototype chain
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * A Fetch-based implementation of the IHttpClient interface
 */
export class FetchHttpClient implements IHttpClient {
  /**
   * Gets JSON data from a service
   *
   * @param options - the request options
   * @returns - the response
   */
  public async getJsonAsync<TResponse>(options: IHttpRequestOptions): Promise<TResponse> {
    const { url, headers, cache } = options;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...headers,
          Accept: "application/json",
        },
        cache: cache === true ? "default" : "no-store",
      });

      if (!response.ok) {
        throw new HttpError(
          `HTTP request failed with error ${response.status} - ${response.statusText}`,
          response.status,
          response.statusText
        );
      }

      return (await response.json()) as TResponse;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      } else {
        const message = error instanceof Error ? error.message : "unknown";
        throw new HttpError(`HTTP request failed with unknown error ${message}`, 0, message);
      }
    }
  }

  /**
   * Posts JSON data to a service and receives a response
   *
   * @param options - the request options
   * @returns - the response
   */
  public async postJsonAsync<TResponse>(options: IHttpRequestOptions): Promise<TResponse> {
    const { url, headers, cache, data } = options;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json;charset=utf-8",
        },
        body: JSON.stringify(data ?? {}),
        cache: cache === true ? "default" : "no-store",
      });

      if (!response.ok) {
        throw new HttpError(
          `HTTP request failed with error ${response.status} - ${response.statusText}`,
          response.status,
          response.statusText
        );
      }

      return (await response.json()) as TResponse;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      } else {
        const message = error instanceof Error ? error.message : "unknown";
        throw new HttpError(`HTTP request failed with unknown error ${message}`, 0, message);
      }
    }
  }
}
